
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { CheckSquare, Clock, Calendar } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { useNavigate } from "react-router-dom";

const ParentDashboard = () => {
  const navigate = useNavigate();
  
  // Mock data
  const upcomingWorkDays = [
    { date: "May 20", time: "9:00 AM - 1:00 PM", role: "Assistant" },
    { date: "June 5", time: "9:00 AM - 1:00 PM", role: "Snack Provider" },
  ];
  
  const tasks = [
    { id: "task1", title: "Clean art supplies", dueDate: "Today", status: "pending" },
    { id: "task2", title: "Prepare snacks for Friday", dueDate: "Tomorrow", status: "pending" },
    { id: "task3", title: "Update emergency contact", dueDate: "May 15", status: "completed" },
  ];
  
  const announcements = [
    { 
      id: "ann1", 
      title: "Summer Camp Registration", 
      content: "Registration for summer camp opens next Monday. Limited spots available.",
      date: "2 hours ago",
    },
    { 
      id: "ann2", 
      title: "Parent-Teacher Conference", 
      content: "Schedule your slot for the upcoming parent-teacher conferences.",
      date: "1 day ago",
    },
  ];
  
  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold tracking-tight">Parent Dashboard</h1>
        <div className="flex gap-2">
          <Button onClick={() => navigate("/tasks")}>View All Tasks</Button>
        </div>
      </div>
      
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Upcoming Work Days</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{upcomingWorkDays.length}</div>
            <p className="text-xs text-muted-foreground">
              Next workday: {upcomingWorkDays[0]?.date}
            </p>
            <div className="mt-4 space-y-2">
              {upcomingWorkDays.map((day, i) => (
                <div key={i} className="flex justify-between items-center text-sm">
                  <div className="flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-muted-foreground" />
                    <span>{day.date}</span>
                  </div>
                  <Badge variant="outline">{day.role}</Badge>
                </div>
              ))}
            </div>
          </CardContent>
          <CardFooter>
            <Button variant="outline" size="sm" className="w-full" onClick={() => navigate("/schedule")}>
              View Schedule
            </Button>
          </CardFooter>
        </Card>
        
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Task Completion</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">33%</div>
            <p className="text-xs text-muted-foreground">
              1 of 3 tasks completed
            </p>
            <div className="mt-4">
              <Progress value={33} className="h-2" />
            </div>
            <div className="mt-4 space-y-2">
              {tasks.map((task) => (
                <div key={task.id} className="flex justify-between items-center text-sm">
                  <div className="flex items-center gap-2">
                    <CheckSquare className={`h-4 w-4 ${task.status === "completed" ? "text-accent" : "text-muted-foreground"}`} />
                    <span className={task.status === "completed" ? "line-through text-muted-foreground" : ""}>
                      {task.title}
                    </span>
                  </div>
                  <span className="text-xs text-muted-foreground">{task.dueDate}</span>
                </div>
              ))}
            </div>
          </CardContent>
          <CardFooter>
            <Button variant="outline" size="sm" className="w-full" onClick={() => navigate("/tasks")}>
              View Tasks
            </Button>
          </CardFooter>
        </Card>
        
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Announcements</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {announcements.map((announcement) => (
              <div key={announcement.id} className="space-y-1">
                <div className="flex items-center gap-2">
                  <h3 className="font-medium text-sm">{announcement.title}</h3>
                  <Badge variant="secondary" className="text-xs">New</Badge>
                </div>
                <p className="text-xs text-muted-foreground">{announcement.content}</p>
                <p className="text-xs text-muted-foreground">{announcement.date}</p>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>
      
      <div>
        <h2 className="text-lg font-semibold mb-4">Monthly Overview</h2>
        <Card>
          <CardContent className="p-6">
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="dashboard-stat">
                  <span className="stat-label">Hours Contributed</span>
                  <span className="stat-value">16 / 20</span>
                </div>
                <div className="dashboard-stat">
                  <span className="stat-label">Tasks Completed</span>
                  <span className="stat-value">8</span>
                </div>
                <div className="dashboard-stat">
                  <span className="stat-label">Workdays Scheduled</span>
                  <span className="stat-value">4</span>
                </div>
                <div className="dashboard-stat">
                  <span className="stat-label">Volunteer Status</span>
                  <Badge className="mt-1">On Track</Badge>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ParentDashboard;
